import { useEffect, useState } from "react";
import { useSessionStore } from "../store/sessionStore";
import { useDocumentStore } from "../store/documentStore";
import { MD } from "./Markdown";
import type { Session } from "../lib/session";
import type { BrainstormCandidate } from "../lib/brainstorm";

// DocumentPanel — the Document lens for brainstorm mode. Renders
// session.document as markdown alongside the chat thread. The AI
// rewrites it on every Ask in brainstorm mode; users can also edit
// it manually via the "edit" toggle.
//
// Per-channel scope — the document is stored on the Session, so it
// syncs through the same GitHub-Contents-API substrate as messages.
// Last-write-wins on concurrent edits (no field-level CRDT; for a
// brainstorm session that's fine).

interface Props {
  session: Session;
  /** Live whiteboard snapshot while the AI streams its rewrite. When
   *  set, takes precedence over session.document — lets users watch
   *  the doc rebuild in real time. App.tsx clears it when the stream
   *  finishes and the final doc is committed to session. */
  pendingDocument?: string;
  /** Mosh-Pit brainstorm candidates awaiting a pick. When non-empty,
   *  the panel renders a tabbed picker instead of the doc body. */
  pendingCandidates?: BrainstormCandidate[];
  /** Commit a candidate as the new session.document. */
  onAcceptCandidate?: (c: BrainstormCandidate) => void;
  /** Drop all candidates without picking — leaves doc unchanged. */
  onDismissCandidates?: () => void;
  /** Fire a reframe — AI rewrites the existing whiteboard to fit
   *  the picked frame's structure. App owns the streaming and the
   *  final commit; the panel just hands the frame label + template
   *  up and waits for the resulting stream to flow back through
   *  pendingDocument. */
  onReframe?: (frameLabel: string, frameTemplate: string) => void;
}

export function DocumentPanel({
  session,
  pendingDocument,
  pendingCandidates,
  onAcceptCandidate,
  onDismissCandidates,
  onReframe,
}: Props) {
  const setChannelDocument = useSessionStore((s) => s.setChannelDocument);
  const undoDocument = useSessionStore((s) => s.undoDocument);
  const addLibraryDocument = useDocumentStore((s) => s.addDocument);
  const [editing, setEditing] = useState(false);
  const [reframing, setReframing] = useState(false);
  const [draft, setDraft] = useState(session.document ?? "");
  const [savedToLib, setSavedToLib] = useState(false);
  const historyDepth = session.document_history?.length ?? 0;

  // When the upstream document changes (AI rewrite, remote sync) and
  // we're NOT actively editing, reset the local draft. If the user is
  // mid-edit we leave their text alone — last-write-wins on save.
  useEffect(() => {
    if (!editing) setDraft(session.document ?? "");
  }, [session.document, editing]);

  function save() {
    setChannelDocument(session.id, draft);
    setEditing(false);
  }

  function cancel() {
    setDraft(session.document ?? "");
    setEditing(false);
  }

  const pickingCandidate = (pendingCandidates?.length ?? 0) > 0;
  const [activeCandidateIdx, setActiveCandidateIdx] = useState(0);
  // When a new batch of candidates lands, jump to the user's most-
  // picked strategy from prior brainstorms instead of always tab 0.
  // Falls back to 0 on a fresh install with no history.
  useEffect(() => {
    if (pickingCandidate && pendingCandidates) {
      setActiveCandidateIdx(preferredCandidateIdx(pendingCandidates));
    }
  }, [pendingCandidates, pickingCandidate]);

  const streaming = pendingDocument !== undefined;
  // While streaming we render the live snapshot regardless of any
  // committed doc — the user watches the new one rebuild. Editing is
  // disabled while the AI is mid-write to avoid races.
  const displayedDoc = streaming ? pendingDocument! : (session.document ?? "");
  const hasDoc = displayedDoc.trim().length > 0;
  const editingNow = editing && !streaming;

  function saveToLibrary() {
    const text = (session.document ?? "").trim();
    if (!text) return;
    try {
      const name = `${session.title} — whiteboard.md`;
      addLibraryDocument(name, text);
      setSavedToLib(true);
      // Reset the saved-flash after a moment so the button is reusable
      // for subsequent rewrites that the user wants to snapshot again.
      setTimeout(() => setSavedToLib(false), 2000);
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    }
  }

  function downloadMarkdown() {
    const text = composeExport(session);
    if (!text) return;
    const blob = new Blob([text], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${slug(session.title)}-brainstorm-${dateStamp()}.md`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <aside className="doc-panel">
      <div className="doc-head">
        <span className="doc-title">whiteboard</span>
        {streaming && <span className="doc-streaming">writing…</span>}
        {!streaming && (session.document ?? "").length > 6000 && (
          <span
            className="doc-compress"
            title="The whiteboard is long enough that the next Ask will silently compress as it rewrites — merging duplicates, pruning dead branches, aiming for ~25% shorter without dropping ideas or attributions."
          >
            compress on
          </span>
        )}
        <span className="doc-spacer" />
        {!editingNow ? (
          <>
            <button
              className="btn btn-ghost"
              onClick={() => setReframing((v) => !v)}
              disabled={streaming || !(session.document ?? "").trim() || !onReframe}
              title={
                streaming ? "wait — the AI is rewriting" :
                !(session.document ?? "").trim() ? "no content to reframe yet" :
                "Reframe — AI rewrites the existing whiteboard into a new structure"
              }
            >
              {reframing ? "Reframe ▴" : "Reframe ▾"}
            </button>
            <button
              className="btn btn-ghost"
              onClick={() => undoDocument(session.id)}
              disabled={streaming || historyDepth === 0}
              title={
                streaming ? "wait — the AI is rewriting" :
                historyDepth === 0 ? "no prior version to undo to" :
                `Undo — restore the previous whiteboard (${historyDepth} step${historyDepth === 1 ? "" : "s"} back available)`
              }
            >
              ← Undo
            </button>
            <button
              className="btn btn-ghost"
              onClick={saveToLibrary}
              disabled={streaming || !(session.document ?? "").trim() || savedToLib}
              title="Save the current whiteboard to your Library so you can attach it to other channels as context."
            >
              {savedToLib ? "Saved ✓" : "→ Library"}
            </button>
            <button
              className="btn btn-ghost"
              onClick={downloadMarkdown}
              disabled={streaming || !(session.document ?? "").trim()}
              title="Download the whiteboard + full chat transcript as a single .md"
            >
              Download
            </button>
            <button
              className="btn btn-ghost"
              onClick={() => setEditing(true)}
              disabled={streaming}
              title={streaming ? "wait — the AI is rewriting" : "Edit the whiteboard"}
            >
              Edit
            </button>
          </>
        ) : (
          <>
            <button className="btn btn-ghost" onClick={cancel}>Cancel</button>
            <button className="btn btn-primary" onClick={save}>Save</button>
          </>
        )}
      </div>

      {reframing && !pickingCandidate && !editingNow && (
        <div className="doc-reframe">
          <div className="doc-reframe-head">
            Reframe the current whiteboard into:
          </div>
          <div className="doc-frames">
            {FRAMES.map((f) => (
              <button
                key={f.label}
                className="doc-frame"
                onClick={() => {
                  if (onReframe) onReframe(f.label, f.template(session.title));
                  rememberFrame(f.label);
                  setReframing(false);
                }}
                title={f.hint}
              >
                <span className="doc-frame-label">{f.label}</span>
                <span className="doc-frame-hint">{f.hint}</span>
              </button>
            ))}
          </div>
          <button className="btn btn-ghost doc-reframe-cancel" onClick={() => setReframing(false)}>
            Cancel
          </button>
        </div>
      )}

      {pickingCandidate && pendingCandidates ? (
        <CandidatesPicker
          candidates={pendingCandidates}
          activeIdx={activeCandidateIdx}
          onPickTab={setActiveCandidateIdx}
          onAccept={(c) => { bumpPickCount(c.strategy); onAcceptCandidate?.(c); }}
          onDismiss={() => onDismissCandidates?.()}
        />
      ) : editingNow ? (
        <textarea
          className="doc-textarea"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="markdown — bullets, sections, anything. the AI rewrites this on every Ask in brainstorm mode."
          spellCheck={false}
          autoFocus
        />
      ) : hasDoc ? (
        <div className="doc-body-wrap">
          <DocOutline doc={displayedDoc} />
          <div id="doc-body-scroll" className={`doc-body ${streaming ? "doc-body-streaming" : ""}`}>
            <MD>{displayedDoc}</MD>
          </div>
        </div>
      ) : (
        <div className="doc-empty">
          <p className="doc-empty-head">Pick a frame to start with</p>
          <p className="doc-empty-sub">
            Or skip — toss an idea into the chat and hit Ask. The AI will seed the
            whiteboard with whatever structure fits.
          </p>
          <div className="doc-frames">
            {FRAMES.map((f) => (
              <button
                key={f.label}
                className="doc-frame"
                onClick={() => {
                  setChannelDocument(session.id, f.template(session.title));
                  rememberFrame(f.label);
                }}
                title={f.hint}
              >
                <span className="doc-frame-label">{f.label}</span>
                <span className="doc-frame-hint">{f.hint}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </aside>
  );
}

interface OutlineEntry { level: 2 | 3; text: string; slug: string; }

/** Auto-generated table of contents from the whiteboard's H2/H3
 *  headings. Renders only when the doc has at least 2 H2 sections —
 *  short docs don't need outline navigation. Click → smooth-scrolls
 *  to the heading in the body via findText (no anchor-id wiring
 *  needed since markdown-to-jsx doesn't emit ids on headings). */
function DocOutline({ doc }: { doc: string }) {
  const entries: OutlineEntry[] = [];
  for (const line of doc.split("\n")) {
    const h2 = line.match(/^##\s+(.+?)\s*$/);
    const h3 = line.match(/^###\s+(.+?)\s*$/);
    if (h2) entries.push({ level: 2, text: h2[1], slug: slug(h2[1]) });
    else if (h3) entries.push({ level: 3, text: h3[1], slug: slug(h3[1]) });
  }
  // Skip the outline entirely when there's nothing structural to
  // navigate — outline-on-flat-doc is just noise.
  const h2Count = entries.filter((e) => e.level === 2).length;
  if (h2Count < 2) return null;

  function jumpTo(text: string) {
    const body = document.getElementById("doc-body-scroll");
    if (!body) return;
    // Find the rendered heading by visible text — markdown-to-jsx
    // doesn't put ids on headings by default and we don't want to
    // fork its rendering just for this.
    const headings = Array.from(body.querySelectorAll("h2, h3"));
    const match = headings.find((h) => (h.textContent ?? "").trim() === text);
    if (match) (match as HTMLElement).scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <aside className="doc-outline">
      <div className="doc-outline-head">outline</div>
      {entries.map((e, i) => (
        <button
          key={`${e.slug}-${i}`}
          className={`doc-outline-link doc-outline-${e.level}`}
          onClick={() => jumpTo(e.text)}
          title={e.text}
        >
          {e.text}
        </button>
      ))}
    </aside>
  );
}

function CandidatesPicker({
  candidates, activeIdx, onPickTab, onAccept, onDismiss,
}: {
  candidates: BrainstormCandidate[];
  activeIdx: number;
  onPickTab: (i: number) => void;
  onAccept: (c: BrainstormCandidate) => void;
  onDismiss: () => void;
}) {
  const active = candidates[Math.min(activeIdx, candidates.length - 1)];
  return (
    <div className="doc-candidates">
      <div className="doc-cand-tabs" role="tablist">
        {candidates.map((c, i) => (
          <button
            key={c.strategy}
            role="tab"
            aria-selected={i === activeIdx}
            className={`doc-cand-tab ${i === activeIdx ? "active" : ""}`}
            onClick={() => onPickTab(i)}
            title={c.hint}
          >
            <span className="doc-cand-tab-label">{c.strategy}</span>
            <span className="doc-cand-tab-hint">{c.hint}</span>
          </button>
        ))}
      </div>
      <div className="doc-cand-body">
        <MD>{active.document}</MD>
      </div>
      <div className="doc-cand-actions">
        <button className="btn btn-ghost" onClick={onDismiss}>
          Dismiss
        </button>
        <span style={{ flex: 1 }} />
        <span className="doc-cand-ack">{active.ack}</span>
        <button className="btn btn-primary" onClick={() => onAccept(active)}>
          Accept {active.strategy}
        </button>
      </div>
    </div>
  );
}

/** Build the combined brainstorm export: whiteboard at top, full chat
 *  transcript below as a fenced section. Single file so the recipient
 *  has both artifacts together — the organized output AND the raw
 *  conversation that produced it. Returns empty string when there's
 *  nothing worth exporting (no doc, no messages). */
function composeExport(session: Session): string {
  const doc = (session.document ?? "").trim();
  const transcript = (session.messages ?? [])
    .filter((m) => m.role !== "system")
    .map((m) => {
      const who = m.role === "assistant"
        ? `**AI${m.model ? ` (${m.model})` : ""}**`
        : `**${m.author ?? "user"}**`;
      const stamp = m.created_at ? ` · _${m.created_at}_` : "";
      return `${who}${stamp}\n\n${m.content}\n`;
    })
    .join("\n---\n\n");

  if (!doc && !transcript) return "";

  const parts: string[] = [];
  parts.push(`# ${session.title}`);
  parts.push(`_exported ${new Date().toISOString()}_`);
  parts.push("");
  if (doc) {
    parts.push("## Whiteboard");
    parts.push("");
    parts.push(doc);
    parts.push("");
  }
  if (transcript) {
    parts.push("## Transcript");
    parts.push("");
    parts.push(transcript);
  }
  return parts.join("\n");
}

interface Frame {
  label: string;
  hint: string;
  template: (title: string) => string;
}

const LAST_FRAME_KEY = "playground.last_brainstorm_frame";
const MP_PICK_COUNTS_KEY = "playground.brainstorm_mp_pick_counts";

function rememberFrame(label: string): void {
  if (typeof localStorage === "undefined") return;
  try { localStorage.setItem(LAST_FRAME_KEY, label); } catch { /* ignore */ }
}

/** Per-strategy accept counts, persisted to localStorage. Used to
 *  default-select the user's most-picked strategy tab when a fresh
 *  Mosh-Pit batch lands — saves them clicking the same tab over
 *  and over once they've established a preference. */
function loadPickCounts(): Record<string, number> {
  if (typeof localStorage === "undefined") return {};
  try {
    const raw = localStorage.getItem(MP_PICK_COUNTS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, number>;
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function bumpPickCount(strategy: string): void {
  if (typeof localStorage === "undefined") return;
  try {
    const counts = loadPickCounts();
    counts[strategy] = (counts[strategy] ?? 0) + 1;
    localStorage.setItem(MP_PICK_COUNTS_KEY, JSON.stringify(counts));
  } catch { /* ignore */ }
}

/** Index into `candidates` of the user's most-picked strategy, or 0
 *  when there's no history yet (or none of the saved strategies match
 *  this batch's candidates — e.g. across a strategy rename). */
function preferredCandidateIdx(candidates: BrainstormCandidate[]): number {
  const counts = loadPickCounts();
  let bestIdx = 0;
  let bestCount = -1;
  for (let i = 0; i < candidates.length; i++) {
    const c = counts[candidates[i].strategy] ?? 0;
    if (c > bestCount) { bestCount = c; bestIdx = i; }
  }
  return bestIdx;
}

/** Returns a seeded template for the user's most-recently-picked
 *  frame, or null if none has been picked yet (or the saved label no
 *  longer matches a known frame across a rename). Toolbar uses this
 *  to skip the picker on subsequent brainstorm-toggles. */
export function seedFromLastFrame(title: string): string | null {
  if (typeof localStorage === "undefined") return null;
  const saved = localStorage.getItem(LAST_FRAME_KEY);
  if (!saved) return null;
  const frame = FRAMES.find((f) => f.label === saved);
  return frame ? frame.template(title) : null;
}

// Starter templates the user picks at first-toggle. The AI follows
// whatever structure already exists in the doc on subsequent rewrites,
// so picking a frame at the start steers the whole brainstorm.
const FRAMES: Frame[] = [
  {
    label: "Open canvas",
    hint: "blank slate — let the AI organize as ideas arrive",
    template: (t) => `# ${t}\n\nIdeas land here as the team contributes. The AI organizes and adds its own.\n`,
  },
  {
    label: "Pros & cons",
    hint: "two columns: arguments for and against",
    template: (t) => `# ${t}\n\n## Pros\n\n## Cons\n`,
  },
  {
    label: "Risks & mitigations",
    hint: "what could go wrong, paired with how we'd handle it",
    template: (t) => `# ${t}\n\n## Risks\n\n## Mitigations\n`,
  },
  {
    label: "User journey",
    hint: "stages the user moves through",
    template: (t) => `# ${t}\n\n## Awareness\n\n## Consideration\n\n## Decision\n\n## Use\n`,
  },
  {
    label: "Themes",
    hint: "cluster ideas into thematic groups as they arrive",
    template: (t) => `# ${t}\n\n## Themes\n\n`,
  },
];

function slug(s: string): string {
  return s.toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48) || "channel";
}

function dateStamp(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}`;
}
